import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Button, Card, Spinner } from '@timely/ui';
import { useSession } from './SessionProvider';

/**
 * Nothing behind this renders until we know who is asking.
 *
 * The three states are kept apart on purpose:
 *
 *   loading  — we do not know yet, so we render neither the screen nor the
 *              sign-in page. Guessing wrong in either direction is a flash of
 *              the wrong thing, and in one direction it is a flash of somebody
 *              else's data.
 *   expired  — a session that ran out. This is what a tab left open all day
 *              actually reaches, and it deserves a sentence rather than a
 *              silent bounce to a login form that looks like a bug.
 *   signed-out — off to sign in, remembering where they were going.
 */
export function RequireAuth() {
    const { state, loading, signOut } = useSession();
    const location = useLocation();

    if (loading) {
        return (
            <div className="min-h-dvh grid place-items-center" role="status" aria-live="polite">
                <span className="sr-only">Checking your session</span>
                <Spinner className="text-accent w-6 h-6" />
            </div>
        );
    }

    if (state === 'expired') {
        return (
            <main className="min-h-dvh grid place-items-center px-4">
                <Card className="max-w-md w-full px-7 py-8 text-center" role="alert">
                    <h1 className="text-[20px] font-bold text-label tracking-[-0.02em]">
                        Your session has expired
                    </h1>
                    <p className="text-[14px] text-label-2 mt-3 leading-relaxed">
                        You were signed out after a period of inactivity. Anything you had saved is
                        safe. Sign in again to carry on where you were.
                    </p>
                    <div className="mt-7">
                        <Button variant="primary" onClick={() => { void signOut(); }}>
                            Sign in again
                        </Button>
                    </div>
                </Card>
            </main>
        );
    }

    if (state === 'signed-out') {
        const returnTo = `${location.pathname}${location.search}`;
        return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
    }

    return <Outlet />;
}

/**
 * Past this point the address has been proved.
 *
 * `create_workspace()` and `accept_invite()` both refuse an unverified account
 * in the database, so this is not the boundary — it is the screen that explains
 * the refusal before somebody runs into it.
 */
export function RequireVerified() {
    const { verified } = useSession();
    if (!verified) return <Navigate to="/verify-email" replace />;
    return <Outlet />;
}
